import React, { useState, useRef, useCallback, useEffect } from 'react';
import Hotkeys from '../Hotkeys'
import { Divider, NavbarHeading, Button, Menu, MenuItem } from '@blueprintjs/core';
import '../constants/ColorCodes.css';
import Stepper from "../components/Stepper/Stepper";
//import MenuBar from '../components/MenuBar/MenuBar';
import ConfigureSessionPage from '../pages/ConfigureSessionPage';
import TeachRPage from '../pages/TeachRPage';
import DashboardPage from '../pages/DashboardPage';
import WebsocketStatusManager from '../components/WebsocketStatus/WebsocketStatusManager';
import SessionSettingsManager from '../features/ConfigurationContent/SessionSettingsManager';
import useDarkMode from '../components/DarkModeToggle/DarkModeToggle';
import TeachRIcon from '../logos/Teach-R_Icon.png'
import './MainPageStyle.css' 

const steps = ['Configure Session', 'Teach-R', 'End Session'];

/**
 * Main page of the application with a horizontal stepper that navigates through the different stages of a session.
 * 1. configuration
 * 2. actual session 
 * 3. evaluation of the session
 * The logo is also displayed on the left and the connection status and menu on the right.
 * @returns {JSX.Element} The rendered `MainPage` component.
 */
const MainPage = () => {
    const [currentStep, setCurrentStep] = useState(0);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isLinksOpen, setIsLinksOpen] = useState(false);
    const menuRef = useRef(null);
    const [darkMode, setDarkMode] = useDarkMode();
    const { saveSettings, triggerLoadFile, resetToDefaultSettings } = SessionSettingsManager();

    const handleNextStep = () => {
        if (currentStep < steps.length - 1) {
            setCurrentStep(currentStep + 1);
        }
        else {
            setCurrentStep(0);
        }
    };

    const linksRef = useRef(null);

    const renderPage = () => {
        switch (currentStep) {
            case 0:
                return <ConfigureSessionPage onNext={handleNextStep} />;
            case 1:
                return <TeachRPage onNext={handleNextStep} />;
            case 2:
                return <DashboardPage onNext={handleNextStep} />;
            default:
                return <div>Unknown Step</div>;
        }
    };

    // close menus if clicked somewhere outside
    const handleClickOutside = useCallback((event) => {
        if (menuRef.current && !menuRef.current.contains(event.target)) {
            setIsMenuOpen(false);
        }
        if (linksRef.current && !linksRef.current.contains(event.target)) {
            setIsLinksOpen(false);
        }
    }, []);

    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [handleClickOutside]);

    // execute selected action and close menu
    const closeMenuAndExecuteAction = (action) => {
        action();
        setIsMenuOpen(false);
    };

    const closeLinkMenuAndExecuteAction = (action) => {
        action();
        setIsLinksOpen(false);
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', backgroundColor: 'var(--background)', width: "100vw", height:"100vh" }}>
            <Hotkeys />
            {/*<MenuBar onStepChange={setCurrentStep} /> */}{/*VERSION WITHOUT DEBUG BUTTONS: <MenuBar />*/}
            <div style={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '0 16px',
                backgroundColor: 'var(--background)',
            }}>
                {/* LOGO + TITLE */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <img src={TeachRIcon} alt="Icon" style={{ height: '40px' }} />
{/*                    <img src={TeachRIcon} alt="Icon" className="menu-bar-icon" />*/}
                    <NavbarHeading>Teach-R</NavbarHeading>
                </div>

                {/*<Stepper steps={steps} currentStep={currentStep} orientation="horizontal" />*/}
                <Stepper steps={steps} currentStep={currentStep} onStepChange={setCurrentStep} orientation="horizontal" />

                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {/* DEBUG BUTTONS */}
                    {/*<Button*/}
                    {/*    text="Page 1"*/}
                    {/*    minimal={false}*/}
                    {/*    className="debug-button"*/}
                    {/*    onClick={() => onStepChange(0)} // Switch to ConfigureSessionPage*/}
                    {/*/>*/}
                    {/*<Button*/}
                    {/*    text="Page 2"*/}
                    {/*    minimal={false}*/}
                    {/*    className="debug-button"*/}
                    {/*    onClick={() => onStepChange(1)} // Switch to TeachRPage*/}
                    {/*/>*/}
                    {/*<Button*/}
                    {/*    text="Page 3"*/}
                    {/*    minimal={false}*/}
                    {/*    className="debug-button"*/}
                    {/*    onClick={() => onStepChange(2)} // Switch to DashboardPage*/}
                    {/*/>*/}

                    {/* WEBSOCKET */}
                    <WebsocketStatusManager />

                    {/* SETTINGS */}
                    <div style={{ position: 'relative' }}>
                        <Button icon="cog" minimal onClick={() => setIsMenuOpen(prev => !prev)} />
                        {isMenuOpen && (
                            <div style={{ position: 'absolute', top: '100%', right: 0, zIndex: 999 }}>
                                <Menu
                                    className="settings-menu">
                                    <MenuItem
                                        icon={darkMode ? "moon" : "flash"}
                                        text={`Dark Mode ${darkMode ? "Off" : "On"}`}
                                        onClick={() => setDarkMode(!darkMode)}
                                    />
                                    <MenuItem
                                        text="Presettings"
                                        icon="settings"
                                    >
                                        <MenuItem
                                            text="Load Settings"
                                            onClick={() => closeMenuAndExecuteAction(triggerLoadFile)}
                                        />
                                        <MenuItem
                                            text="Save Settings"
                                            onClick={() => closeMenuAndExecuteAction(saveSettings)}
                                        />
                                        <MenuItem
                                            text="Default Settings"
                                            onClick={() => closeMenuAndExecuteAction(resetToDefaultSettings)}
                                        />
                                    </MenuItem>
                                    <MenuItem
                                        className="settings-menu"
                                        text="Links"
                                        icon="document-share"
                                    >
                                        <MenuItem
                                            text="Technik: www.teach-r.de"
                                            icon="share"
                                            onClick={() => closeLinkMenuAndExecuteAction(() => window.open('https://gitup.uni-potsdam.de/mm_vr/vr-klassenzimmer/-/wikis/home', '_blank'))}
                                        />
                                        <MenuItem
                                            text="Forschung: https://www.uni-potsdam.de/de/multimedia/projekte/anwendungen/vr-labor-klassenzimmer"
                                            icon="share"
                                            onClick={() => closeLinkMenuAndExecuteAction(() => window.open('https://www.uni-potsdam.de/de/multimedia/projekte/anwendungen/vr-labor-klassenzimmer', '_blank'))}
                                        />
                                        <MenuItem
                                            text="Lehre und Historie: https://www.e-teaching.org/praxis/erfahrungsberichte/simulationsbasierte-lehramtausbildung-mit-teach-r"
                                            icon="share"
                                            onClick={() => closeLinkMenuAndExecuteAction(() => window.open('https://www.e-teaching.org/praxis/erfahrungsberichte/simulationsbasierte-lehramtausbildung-mit-teach-r', '_blank'))}
                                        />
                                        <MenuItem
                                            text="Bei Fragen und Anmerkungen, melden Sie sich gern bei Axel Wiepke <axel.wiepke@uni-potsdam.de>"
                                            icon="share"
                                            onClick={() => closeLinkMenuAndExecuteAction(() => window.open('mailto:axel.wiepke@uni-potsdam.de', '_blank'))}
                                        />
                                    </MenuItem>
                                </Menu>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <Divider style={{ backgroundColor: 'var(--gray_1)' }} />

            <div style={{ padding: '1rem' }}>{renderPage()}</div>
        </div>
    );
};


export default MainPage;